import * as React from 'react'
import { useCallback, useState } from 'react'
import * as ReactDOM from 'react-dom'

import { Avatar, Box, Text, matchText, useMemo } from '@factorialco/deprecated-design-system'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { $createTextNode, $getSelection, TextNode } from 'lexical'

import { observer } from 'mobx-react'

import { useEditor } from 'modules/core/components/TextEditor/useEditor'
import buildUrl from 'modules/core/lib/buildUrl'

import { $createMentionNode } from '../nodes/MentionNode'
import * as styles from './MentionsPlugin.vanilla.css'

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentMentionsRegex.PUNCTUATION

const TRIGGERS = ['@'].join('')

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
)

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5

export interface MentionedUser {
  id: number
  employeeId?: number
  fullname: string
  picture: string
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text)

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text)
  } else {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1] || []
    const matchingString = match[3] || ''

    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace?.length,
        matchingString,
        replaceableString: match[2] || '',
      }
    }
  }
  return null
}

class MentionMenuOption extends MenuOption {
  name: string
  id: number
  picture: JSX.Element
  employeeId?: number

  constructor(user: MentionedUser, picture: JSX.Element) {
    super(user.fullname)
    this.name = user.fullname
    this.id = user.id
    this.employeeId = user.employeeId
    this.picture = picture
  }
}

function MentionsTypeaheadMenuItem({
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  option: MentionMenuOption
}) {
  return (
    <Box
      key={option.key}
      role="option"
      flexDirection="row"
      alignItems="center"
      paddingY="s6"
      paddingX="s16"
      outline="none"
      cursor="pointer"
      gap="s8"
      background={isSelected ? 'grey200' : undefined}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.picture}
      <Text.Normal size="200">{option.name}</Text.Normal>
    </Box>
  )
}

interface Props {
  users: MentionedUser[] | undefined
}

function MentionsPlugin({ users = [] }: Props): JSX.Element | null {
  const editor = useEditor()
  const [queryString, setQueryString] = useState<string | null>(null)

  const options = useMemo(() => {
    if (!queryString) {
      return []
    }

    const results = users.filter((user) => matchText(queryString, user.fullname))
    const resultsToShow = results.slice(0, SUGGESTION_LIST_LENGTH_LIMIT)

    return resultsToShow.map(
      (result) =>
        new MentionMenuOption(
          result,
          <Avatar size="s24" url={buildUrl(result.picture)} label={result.fullname} />
        )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const onSelectOption = useCallback(
    (selectedOption: MentionMenuOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption)

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode)
        }
        // highlight the username text
        mentionNode.select()
        // add a space after the mentioned user name
        $getSelection()?.insertNodes([$createTextNode(' ')])

        closeMenu()
      })
    },
    [editor]
  )

  const checkForMentionMatch = useCallback((text: string) => checkForAtSignMentions(text, 1), [])

  if (!editor.isEditable()) {
    return null
  }

  return (
    <LexicalTypeaheadMenuPlugin<MentionMenuOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        const menu = anchorElementRef.current

        if (menu && options.length > 0) {
          return ReactDOM.createPortal(
            <div
              className={styles.mentionPopover}
              style={{ top: menu.offsetTop, left: menu.offsetLeft }}
            >
              <Box
                component="ul"
                paddingY="s12"
                borderRadius={{ all: 'abs008' }}
                background="grey000White"
                boxShadow="s100"
              >
                {options.map((option, i: number) => (
                  <MentionsTypeaheadMenuItem
                    index={i}
                    isSelected={selectedIndex === i}
                    onClick={() => {
                      setHighlightedIndex(i)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => {
                      setHighlightedIndex(i)
                    }}
                    key={option.key}
                    option={option}
                  />
                ))}
              </Box>
            </div>,
            document.body
          )
        }

        return null
      }}
    />
  )
}

export default observer(MentionsPlugin)
